import { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mailLanguageFor } from '@/hooks/useProfileLanguage';
import { getSupabase, getAuthToken } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { useSupabase } from '@/components/providers/SupabaseProvider';
import { useAuthReady } from '@/hooks/useAuthReady';
import { subscriptionKeys } from '@/hooks/useSubscription';

const inviteClaimKey = (userId: string) => `bookiapp:invite-claim:${userId}`;

function inviteClaimDoneThisSession(userId: string): boolean {
  try {
    return sessionStorage.getItem(inviteClaimKey(userId)) === '1';
  } catch {
    return false;
  }
}

function markInviteClaimDone(userId: string): void {
  try {
    sessionStorage.setItem(inviteClaimKey(userId), '1');
  } catch {
    /* private mode / storage blocked — the ref still prevents repeats */
  }
}

/**
 * Hook to sync Clerk user to Supabase profiles table
 * This is a fallback mechanism - primary sync should happen via Clerk webhook
 */
export function useUserSync() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { setProfile, setLoading, setError, clearAuth } = useAuthStore();
  const { isLoading: authLoading, hasJwtTemplate } = useSupabase();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const hasInitialized = useRef(false);
  const wasSignedIn = useRef(false);
  // Clerk id whose profile row was confirmed (fetched / created) in THIS
  // session — the persisted store profile from localStorage doesn't count.
  const [syncedUserId, setSyncedUserId] = useState<string | null>(null);
  const claimAttemptedFor = useRef<string | null>(null);
  const { isReady: authReady, userId: readyUserId } = useAuthReady();

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn || !user) {
      clearAuth();
      hasInitialized.current = false;
      setSyncedUserId(null);
      claimAttemptedFor.current = null;
      // On real logout (was signed in, now isn't), drop all React Query state
      // so a different user logging in next can't briefly see the previous
      // user's cached queries (5min default staleTime would otherwise leak).
      // We only clear on the transition, not on the initial mount where
      // wasSignedIn is already false.
      if (wasSignedIn.current) {
        queryClient.clear();
        wasSignedIn.current = false;
      }
      return;
    }

    wasSignedIn.current = true;

    // Wait for SupabaseProvider to finish initializing JWT
    if (authLoading) return;

    // Double-check: JWT token must actually be set before querying
    // This prevents race conditions where authLoading flips to false
    // before setAuthToken() has been called
    if (hasJwtTemplate && !getAuthToken()) return;

    // Prevent double initialization
    if (hasInitialized.current) return;

    const syncUser = async () => {
      setLoading(true);
      hasInitialized.current = true;
      if (import.meta.env.DEV) console.log('Syncing user to Supabase (Clerk ID:', user.id, ')');

      const db = getSupabase();
      const done = (profile: Parameters<typeof setProfile>[0]) => {
        setProfile(profile);
        setSyncedUserId(user.id);
      };
      // A profile created in this session (here or by the webhook racing us)
      // may already carry a plan: the dashboard gate must re-read billing info.
      const refreshBilling = () => {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
        queryClient.invalidateQueries({ queryKey: ['profile-access-check'] });
      };

      try {
        const primaryEmail = user.emailAddresses[0]?.emailAddress;

        // First, try to get existing profile by Clerk ID
        const { data: existingProfile, error: fetchError } = await db
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        // RLS blocked (406) means JWT isn't working - don't proceed with fallbacks
        if (fetchError && fetchError.code !== 'PGRST116') {
          throw fetchError;
        }

        if (existingProfile) {
          done(existingProfile);
          return;
        }

        // Not found by ID (PGRST116) - try by email (for manually created admin profiles)
        if (fetchError?.code === 'PGRST116' && primaryEmail) {
          const { data: profileByEmail, error: emailError } = await db
            .from('profiles')
            .select('*')
            .eq('email', primaryEmail)
            .single();

          // If email lookup also fails with non-404 error, stop
          if (emailError && emailError.code !== 'PGRST116') {
            throw emailError;
          }

          if (profileByEmail) {
            // Update the profile ID to match Clerk ID
            const { data: updatedProfile, error: updateError } = await db
              .from('profiles')
              .update({ id: user.id, updated_at: new Date().toISOString() })
              .eq('email', primaryEmail)
              .select()
              .single();

            if (!updateError && updatedProfile) {
              done(updatedProfile);
              return;
            }
            // If update fails, still use the existing profile
            setProfile(profileByEmail);
            return;
          }
        }

        // Profile doesn't exist anywhere, create it (fallback for when webhook didn't fire)
        if (fetchError?.code === 'PGRST116') {
          const primaryPhone = user.phoneNumbers[0]?.phoneNumber;

          if (!primaryEmail) {
            throw new Error('User has no email address');
          }

          // The language the host is using the app in (mails follow profiles.language).
          const uiLanguage = (i18n.resolvedLanguage || i18n.language || '').slice(0, 2);
          const newProfile = {
            id: user.id,
            email: primaryEmail,
            first_name: user.firstName,
            last_name: user.lastName,
            phone: primaryPhone || null,
            avatar_url: user.imageUrl,
            role: 'landlord' as const,
            subscription_status: 'free' as const,
            subscription_plan: 'free' as const,
            max_properties: 3,
            language: mailLanguageFor(uiLanguage),
          };

          const { data: createdProfile, error: insertError } = await db
            .from('profiles')
            .insert(newProfile)
            .select()
            .single();

          if (insertError) {
            // 23505: the Clerk webhook inserted the row meanwhile — use it.
            if (insertError.code === '23505') {
              const { data: racedProfile } = await db
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .maybeSingle();
              if (racedProfile) {
                done(racedProfile);
                refreshBilling();
                return;
              }
            }
            console.error('Failed to create profile:', insertError);
            throw insertError;
          }

          done(createdProfile);
          refreshBilling();
          return;
        }
      } catch (error) {
        console.error('User sync error:', error);
        setError(error instanceof Error ? error.message : 'Failed to sync user');
      }
    };

    syncUser();
  }, [isLoaded, isSignedIn, user, authLoading, hasJwtTemplate, setProfile, setLoading, setError, clearAuth, queryClient]);

  // Admin invitation fallback: once per session, after this user's profile
  // exists, apply a pending invitation for the profile's e-mail
  // (claim_my_invitation() is caller-checked, so it waits for the Clerk token).
  // The Clerk webhook normally does this at sign-up; this covers a late or
  // failed webhook. Errors are ignored (next session retries).
  useEffect(() => {
    if (!authReady || !readyUserId || syncedUserId !== readyUserId) return;
    if (claimAttemptedFor.current === readyUserId) return;
    claimAttemptedFor.current = readyUserId;
    if (inviteClaimDoneThisSession(readyUserId)) return;

    const claim = async () => {
      try {
        const db = getSupabase();
        const { data, error } = await db.rpc('claim_my_invitation');
        if (error) {
          if (import.meta.env.DEV) console.warn('claim_my_invitation failed (ignored):', error.message);
          return;
        }
        markInviteClaimDone(readyUserId);
        const applied = (data as { applied?: boolean } | null)?.applied === true;
        if (!applied) return;

        // The plan changed under us — reload the profile and re-read billing
        // info (the dashboard gate + limits are keyed under these).
        const { data: fresh } = await db.from('profiles').select('*').eq('id', readyUserId).maybeSingle();
        if (fresh) setProfile(fresh);
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
        queryClient.invalidateQueries({ queryKey: ['profile-access-check'] });
      } catch (err) {
        if (import.meta.env.DEV) console.warn('claim_my_invitation threw (ignored):', err);
      }
    };
    void claim();
  }, [authReady, readyUserId, syncedUserId, setProfile, queryClient]);
}

export default useUserSync;
